import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ClipboardList, BookOpen, Zap, Trophy, BookMarked, ChevronRight, Star, Printer } from "lucide-react";
import { API_BASE } from "../config";
import "../styles/resultsPage.css";
import apiFetch from "../apiFetch";
import LoadingOverlay from "../components/LoadingOverlay";

const FILTERS = [
  { key: "all",               label: "All",              icon: <ClipboardList size={15} /> },
  { key: "comprehension",     label: "Comprehension",    icon: <BookOpen size={15} /> },
  { key: "mcq",               label: "MCQ Master",       icon: <Zap size={15} /> },
  { key: "ranked",            label: "Ranked",           icon: <Trophy size={15} /> },
  { key: "book_comprehension",label: "Book",             icon: <BookMarked size={15} /> },
];

const TYPE_LABELS = {
  comprehension:      { label: "Comprehension",  color: "indigo" },
  mcq:                { label: "MCQ Master",     color: "red" },
  ranked:             { label: "Ranked",         color: "yellow" },
  book_comprehension: { label: "Book",           color: "purple" },
};

const TYPE_PRINT_LABELS = {
  comprehension:      "Comprehension",
  mcq:                "MCQ Master",
  ranked:             "Ranked",
  book_comprehension: "Book Comprehension",
};

function formatDateLong(ts) {
  return new Date(ts).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function buildPrintHtml(result) {
  const { game_type, title, score, max_score, played_at, result_json: data } = result;
  const pct = max_score > 0 ? Math.round((score / max_score) * 100) : 0;
  const typeLabel = TYPE_PRINT_LABELS[game_type] || game_type;

  const passageHtml = data?.passage
    ? `<h2>Passage</h2><div class="passage">${data.passage}</div>`
    : "";

  const writtenRows = (questions, userAnswers, markingResults) => {
    const results = markingResults?.results || [];
    return questions.map((q, i) => {
      const marked = results[i] || {};
      return `
        <div class="q-block">
          <div class="q-header">
            <span class="q-num">Q${i + 1}</span>
            <span class="q-marks">${marked.marksAwarded ?? "—"} / ${q.marks ?? "?"} marks</span>
          </div>
          <p class="q-text">${q.question}</p>
          <div class="section">
            <div class="section-label">Your Answer</div>
            <div class="answer-box">${userAnswers[i] || "<em>No answer</em>"}</div>
          </div>
          ${marked.explanation ? `<div class="section"><div class="section-label">Feedback</div><p>${marked.explanation}</p></div>` : ""}
        </div>`;
    }).join("");
  };

  const mcqRows = (questions, userAnswers) => {
    const optEntries = (opts) => Array.isArray(opts)
      ? opts.map((t, j) => [String(j), t])
      : Object.entries(opts || {});
    return questions.map((q, i) => {
      const ua = userAnswers[i];
      const correct = ua === q.correct;
      const opts = optEntries(q.options).map(([key, text]) => {
        const isCorrect = key === q.correct;
        const isUser = key === ua;
        const bg = isCorrect ? "#dcfce7" : isUser ? "#fee2e2" : "#f9fafb";
        const border = isCorrect ? "#86efac" : isUser ? "#fca5a5" : "#e5e7eb";
        return `<div class="opt" style="background:${bg};border-color:${border}"><strong>${key}</strong> ${text}</div>`;
      }).join("");
      return `
        <div class="q-block" style="border-color:${correct ? "#86efac" : "#fca5a5"};background:${correct ? "#f0fdf4" : "#fef2f2"}">
          <div class="q-header">
            <span class="q-num">Q${i + 1}</span>
            <span style="font-weight:700;color:${correct ? "#16a34a" : "#dc2626"}">${correct ? "✓ Correct" : "✗ Wrong"}</span>
          </div>
          <p class="q-text">${q.question}</p>
          <div class="opts">${opts}</div>
        </div>`;
    }).join("");
  };

  let questionsHtml = "";
  if (game_type === "comprehension") {
    questionsHtml = `<h2>Questions</h2>${writtenRows(data?.questions || [], data?.userAnswers || {}, data?.markingResults)}`;
  } else if (game_type === "mcq") {
    questionsHtml = `<h2>Questions</h2>${mcqRows(data?.questions || [], data?.userAnswers || {})}`;
  } else if (game_type === "ranked") {
    const mcqSection = data?.mcqQuestions?.length
      ? `<h2>Multiple Choice</h2>${mcqRows(data.mcqQuestions, data.mcqAnswers || {})}`
      : "";
    const writtenSection = data?.writtenQuestions?.length
      ? `<h2>Written</h2>${writtenRows(data.writtenQuestions, data.writtenAnswers || {}, { results: data.markingResults?.writtenResults || data.markingResults?.results || [] })}`
      : "";
    questionsHtml = mcqSection + writtenSection;
  } else if (game_type === "book_comprehension") {
    questionsHtml = data?.type === "mcq"
      ? `<h2>Questions</h2>${mcqRows(data?.questions || [], data?.userAnswers || {})}`
      : `<h2>Questions</h2>${writtenRows(data?.questions || [], data?.userAnswers || {}, data?.markingResults)}`;
  }

  return `<!DOCTYPE html><html><head>
    <meta charset="utf-8"/>
    <title>${title}</title>
    <style>
      body { font-family: Georgia, serif; color: #1f2937; margin: 0; padding: 32px; font-size: 13px; }
      h1 { font-size: 20px; margin: 0 0 4px; }
      .meta { font-size: 12px; color: #6b7280; margin-bottom: 6px; }
      .score { font-size: 13px; font-weight: 700; margin-bottom: 24px; }
      h2 { font-size: 15px; margin: 24px 0 10px; color: #374151; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; }
      .passage { background: #f9fafb; border-left: 3px solid #6366f1; padding: 14px 16px; border-radius: 4px; line-height: 1.7; margin-bottom: 8px; white-space: pre-wrap; }
      .q-block { border: 1px solid #e5e7eb; border-radius: 6px; padding: 16px; margin-bottom: 12px; break-inside: avoid; }
      .q-header { display: flex; justify-content: space-between; margin-bottom: 8px; }
      .q-num { font-weight: 700; color: #4f46e5; }
      .q-text { font-size: 13px; margin: 0 0 10px; line-height: 1.5; }
      .section { margin-top: 8px; }
      .section-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #9ca3af; margin-bottom: 4px; }
      .answer-box { background: #f3f4f6; border-radius: 4px; padding: 8px 12px; line-height: 1.5; white-space: pre-wrap; }
      .opts { display: flex; flex-direction: column; gap: 6px; }
      .opt { padding: 6px 10px; border-radius: 4px; border: 1px solid #e5e7eb; font-size: 13px; }
      @media print { body { padding: 16px; } }
    </style>
  </head><body>
    <h1>${typeLabel} — ${title}</h1>
    <p class="meta">${formatDateLong(played_at)}</p>
    <p class="score">Score: ${score} / ${max_score} (${pct}%)</p>
    ${passageHtml}
    ${questionsHtml}
  </body></html>`;
}

function ScorePill({ score, maxScore }) {
  const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  const cls = pct >= 70 ? "good" : pct >= 40 ? "mid" : "low";
  return (
    <span className={`rp-score-pill ${cls}`}>
      <Star size={11} aria-hidden="true" /> {score}/{maxScore}
    </span>
  );
}

function formatDate(ts) {
  return new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function ResultsPage() {
  const navigate = useNavigate();
  const userId = parseInt(localStorage.getItem("userId"), 10);
  const [activeFilter, setActiveFilter] = useState("all");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [printingId, setPrintingId] = useState(null);

  useEffect(() => { document.title = "My Results — Owllet Reading Quest"; }, []);

  const handlePrint = useCallback(async (e, id) => {
    e.stopPropagation();
    setPrintingId(id);
    try {
      const res = await apiFetch(`/api/results/detail/${id}`);
      if (!res.ok) throw new Error("Not found");
      const result = await res.json();
      const html = buildPrintHtml(result);
      const win = window.open("", "_blank");
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 400);
    } catch {
      alert("Could not load result for printing.");
    } finally {
      setPrintingId(null);
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const q = activeFilter !== "all" ? `?type=${activeFilter}` : "";
    apiFetch(`/api/results/${userId}${q}`)
      .then((r) => r.json())
      .then((data) => { setResults(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [userId, activeFilter]);

  if (loading && results.length === 0) return <LoadingOverlay message="Loading your results…" />;

  if (error) return (
    <div className="rp-page">
      <div className="rp-header">
        <button className="rp-back-btn" onClick={() => navigate("/statisticsPage")}>
          <ArrowLeft size={16} /> Back
        </button>
        <div className="rp-header-center"><ClipboardList size={20} /><h1>My Results</h1></div>
        <div style={{ width: 80 }} />
      </div>
      <div className="rp-body">
        <div className="rp-empty">
          <p>Failed to load results.</p>
          <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem", justifyContent: "center" }}>
            <button className="rp-filter-btn active" onClick={() => { setError(false); setLoading(true); }}>Retry</button>
            <button className="rp-filter-btn" onClick={() => window.location.reload()}>Reload Page</button>
            <Link to="/home" className="rp-filter-btn">Go Home</Link>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="rp-page">
      <div className="rp-header">
        <button className="rp-back-btn" onClick={() => navigate("/statisticsPage")}>
          <ArrowLeft size={16} /> Back
        </button>
        <div className="rp-header-center">
          <ClipboardList size={20} />
          <h1>My Results</h1>
        </div>
        <div style={{ width: 80 }} />
      </div>

      {/* Filter tabs */}
      <div className="rp-filters" role="group" aria-label="Filter results by game type">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`rp-filter-btn ${activeFilter === f.key ? "active" : ""}`}
            onClick={() => setActiveFilter(f.key)}
            aria-pressed={activeFilter === f.key}
          >
            <span aria-hidden="true">{f.icon}</span> {f.label}
          </button>
        ))}
      </div>

      <div className="rp-body">
        {results.length === 0 ? (
          <div className="rp-empty">
            <ClipboardList size={40} aria-hidden="true" />
            <p>No results yet. Complete a game to see it here!</p>
          </div>
        ) : (
          <div className="rp-list">
            {results.map((r) => {
              const type = TYPE_LABELS[r.game_type] || { label: r.game_type, color: "grey" };
              return (
                <button
                  key={r.id}
                  className="rp-card"
                  onClick={() => navigate(`/my-results/${r.id}`)}
                >
                  <span className={`rp-type-badge ${type.color}`}>{type.label}</span>
                  <div className="rp-card-body">
                    <span className="rp-title">{r.title}</span>
                    <span className="rp-date">{formatDate(r.played_at)}</span>
                  </div>
                  <div className="rp-card-right">
                    <ScorePill score={r.score} maxScore={r.max_score} />
                    <button
                      className="rp-print-btn"
                      onClick={(e) => handlePrint(e, r.id)}
                      aria-label={printingId === r.id ? "Loading print…" : "Print result"}
                      disabled={printingId === r.id}
                    >
                      <Printer size={14} aria-hidden="true" />
                    </button>
                    <ChevronRight size={16} className="rp-chevron" aria-hidden="true" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
